import type { ProjectDefinition } from "../../../types";
import { type FuncionProjectSpec, NOMBRES, conditions, seededRandom } from "../spec";
import { fecha, fechaMes, sifecha } from "./calendario";

const SHEET = "FE Personal";
const TABLE = "Personal";
const CORTE = fecha(2026, 6, 30);

const rnd = seededRandom(3003);
// Empleado, Nacimiento, Ingreso
const ROWS = NOMBRES.slice(0, 30).map((nombre, i) => {
  // Two people have their birthday or anniversary exactly on the cutoff day.
  const nacimiento = i === 4 ? fecha(1990, 6, 30) : fecha(1965, 1, 1) + rnd.int(0, 13500);
  const ingreso = i === 9 ? fecha(2021, 6, 30) : fecha(2003, 1, 1) + rnd.int(0, 8500);
  return [nombre, nacimiento, ingreso] as [string, number, number];
});

type Row = (typeof ROWS)[number];
const antig = (r: Row) => sifecha(r[2], CORTE, "Y");
/** Vacaciones según la LFT reformada en 2023. */
const vacaciones = (a: number) => (a < 1 ? 0 : a <= 5 ? 10 + 2 * a : 20 + 2 * Math.ceil((a - 5) / 5));

export const spec: FuncionProjectSpec = {
  projectId: "funciones-antiguedad",
  sheets: [
    {
      name: SHEET,
      title: "Antigüedad, edad y vacaciones",
      subtitle: "Expedientes de Recursos Humanos — todo se calcula a la fecha de corte",
      tables: [
        {
          name: TABLE,
          anchor: "A4",
          headers: ["Empleado", "Nacimiento", "Ingreso"],
          rows: ROWS,
          answerColumns: ["Edad", "Antigüedad", "Meses extra", "Antigüedad texto", "Años exactos", "Vacaciones", "Próximo cumpleaños"],
          formats: {
            Nacimiento: "dd/mm/yyyy",
            Ingreso: "dd/mm/yyyy",
            "Años exactos": "0.00",
            "Próximo cumpleaños": "dd/mm/yyyy",
          },
        },
      ],
      inputs: [{ address: "M4", label: "Fecha de corte", value: CORTE, format: "dd/mm/yyyy" }],
    },
  ],
  checks: {
    1: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Edad", fns: ["DATEDIF"],
      expected: ROWS.map((r) => sifecha(r[1], CORTE, "Y")),
    },
    2: { kind: "column", sheet: SHEET, table: TABLE, column: "Antigüedad", fns: ["DATEDIF"], expected: ROWS.map(antig) },
    3: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Meses extra", fns: ["DATEDIF"],
      expected: ROWS.map((r) => sifecha(r[2], CORTE, "YM")),
    },
    4: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Antigüedad texto", fns: ["DATEDIF"], minCounts: { DATEDIF: 2 },
      exact: true,
      expected: ROWS.map((r) => `${antig(r)} años, ${sifecha(r[2], CORTE, "YM")} meses`),
    },
    5: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Años exactos", fns: ["YEARFRAC"],
      expected: ROWS.map((r) => (CORTE - r[2]) / 365),
    },
    6: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Vacaciones", fns: ["IFS", "ROUNDUP"],
      expected: ROWS.map((r) => vacaciones(antig(r))),
    },
    7: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Próximo cumpleaños", fns: ["EDATE", "DATEDIF"],
      expected: ROWS.map((r) => fechaMes(r[1], (sifecha(r[1], CORTE, "Y") + 1) * 12)),
    },
  },
};

const F = (text: string) => `<span class="formula">${text}</span>`;

export const project: ProjectDefinition = {
  id: "funciones-antiguedad",
  title: "SIFECHA y FRAC.AÑO: Antigüedad, edad y vacaciones",
  subtitle: "Años y meses cumplidos, no aproximados",
  description:
    "Recursos Humanos necesita, para cada uno de 30 empleados, su edad, su antigüedad exacta, los días de vacaciones que le tocan por ley y la fecha de su próximo cumpleaños.\n\nRestar fechas y dividir entre 365 da errores alrededor de los aniversarios. SIFECHA cuenta años y meses cumplidos de verdad. No aparece en la lista de funciones de Excel (es una función \"oculta\"), pero funciona.",
  topics: [
    "SIFECHA con \"Y\", \"M\" y \"YM\"",
    "Antigüedad en texto: años y meses",
    "FRAC.AÑO para años con decimales",
    "Vacaciones según la LFT (reforma 2023)",
    "Próximo cumpleaños con FECHA.MES",
  ],
  tasks: [
    {
      id: 1,
      level: "basico",
      title: "Tarea 1: Edad",
      description:
        "En la hoja <strong>FE Personal</strong>, columna <strong>Edad</strong>: años cumplidos desde el <strong>Nacimiento</strong> hasta la <strong>Fecha de corte</strong> de <strong>M4</strong>.",
      solution: [
        `Selecciona ${F("D5")}.`,
        `SIFECHA(inicio, fin, unidad). Con ${F('"Y"')} devuelve años completos. Excel no te la sugiere al escribir; escríbela completa.`,
        "En la vida real usarías HOY() como fecha fin; aquí usamos $M$4 para que el resultado no cambie cada día.",
        `Escribe ${F('=SIFECHA([@Nacimiento],$M$4,"Y")')}.`,
      ],
      expectedResult: "Edad en años cumplidos",
      finalFormula: '=SIFECHA([@Nacimiento],$M$4,"Y")',
    },
    {
      id: 2,
      level: "basico",
      title: "Tarea 2: Antigüedad en años",
      description: "En la columna <strong>Antigüedad</strong>: años completos desde el <strong>Ingreso</strong> hasta la fecha de corte.",
      solution: [`Selecciona ${F("E5")}.`, `Escribe ${F('=SIFECHA([@Ingreso],$M$4,"Y")')}.`],
      expectedResult: "Años de antigüedad",
      finalFormula: '=SIFECHA([@Ingreso],$M$4,"Y")',
    },
    {
      id: 3,
      level: "intermedio",
      title: "Tarea 3: Meses además de los años",
      description:
        "En la columna <strong>Meses extra</strong>: los meses cumplidos <strong>además</strong> de los años completos (de 0 a 11). Si alguien lleva 3 años y 5 meses, aquí va 5.",
      solution: [
        `Selecciona ${F("F5")}.`,
        `La unidad ${F('"YM"')} cuenta los meses ignorando los años.`,
        `Escribe ${F('=SIFECHA([@Ingreso],$M$4,"YM")')}.`,
      ],
      expectedResult: "0 a 11",
      finalFormula: '=SIFECHA([@Ingreso],$M$4,"YM")',
    },
    {
      id: 4,
      level: "intermedio",
      title: "Tarea 4: Antigüedad en texto",
      description: "En la columna <strong>Antigüedad texto</strong>: escribe la antigüedad como <strong>3 años, 5 meses</strong> (exactamente con ese formato).",
      solution: [
        `Selecciona ${F("G5")}.`,
        `Une dos SIFECHA con texto usando ${F("&")}.`,
        `Escribe ${F('=SIFECHA([@Ingreso],$M$4,"Y")&" años, "&SIFECHA([@Ingreso],$M$4,"YM")&" meses"')}.`,
      ],
      expectedResult: "3 años, 5 meses",
      finalFormula: '=SIFECHA([@Ingreso],$M$4,"Y")&" años, "&SIFECHA([@Ingreso],$M$4,"YM")&" meses"',
    },
    {
      id: 5,
      level: "intermedio",
      title: "Tarea 5: Años con decimales",
      description:
        "En la columna <strong>Años exactos</strong>: la antigüedad en años <strong>con decimales</strong> (por ejemplo 3.42), usando <strong>FRAC.AÑO</strong> con base <strong>3</strong> (días reales ÷ 365).",
      solution: [
        `Selecciona ${F("H5")}.`,
        "FRAC.AÑO(inicio, fin, base) devuelve la fracción de año entre dos fechas. Sirve para prorratear aguinaldos, primas o intereses.",
        `Escribe ${F("=FRAC.AÑO([@Ingreso],$M$4,3)")}.`,
      ],
      expectedResult: "3.42",
      finalFormula: "=FRAC.AÑO([@Ingreso],$M$4,3)",
    },
    {
      id: 6,
      level: "reto",
      title: "Tarea 6: Días de vacaciones (LFT 2023)",
      description: conditions(
        "En la columna <strong>Vacaciones</strong>: los días que le tocan a cada empleado según su <strong>Antigüedad</strong> (Tarea 2):",
        [
          "Menos de 1 año → <strong>0</strong>",
          "De 1 a 5 años → <strong>12, 14, 16, 18, 20</strong> (10 + 2 × años)",
          "De 6 años en adelante → <strong>+2 días por cada 5 años</strong>: 6–10 → 22, 11–15 → 24, 16–20 → 26…",
        ]
      ),
      solution: [
        `Selecciona ${F("I5")}.`,
        `Para 6 años o más: ${F("20+2*REDONDEAR.MAS(([@Antigüedad]-5)/5,0)")} cuenta cuántos bloques de 5 años lleva después del quinto.`,
        `Escribe ${F("=SI.CONJUNTO([@Antigüedad]<1,0,[@Antigüedad]<=5,10+2*[@Antigüedad],VERDADERO,20+2*REDONDEAR.MAS(([@Antigüedad]-5)/5,0))")}.`,
      ],
      expectedResult: "12 a 28+ días",
      finalFormula:
        "=SI.CONJUNTO([@Antigüedad]<1,0,[@Antigüedad]<=5,10+2*[@Antigüedad],VERDADERO,20+2*REDONDEAR.MAS(([@Antigüedad]-5)/5,0))",
    },
    {
      id: 7,
      level: "reto",
      title: "Tarea 7: Próximo cumpleaños",
      description: conditions("En la columna <strong>Próximo cumpleaños</strong>: la fecha del siguiente cumpleaños <strong>después</strong> de la fecha de corte:", [
        "La persona ya cumplió <strong>Edad</strong> años (SIFECHA)",
        "Su próximo cumpleaños es el número <strong>Edad + 1</strong>",
        "Esa fecha es el Nacimiento + <strong>(Edad + 1) × 12 meses</strong>",
      ]),
      solution: [
        `Selecciona ${F("J5")}.`,
        "FECHA.MES suma meses exactos, así que el cumpleaños cae siempre en el mismo día y mes.",
        `Escribe ${F('=FECHA.MES([@Nacimiento],(SIFECHA([@Nacimiento],$M$4,"Y")+1)*12)')}.`,
        "Revisa a quien cumple años justo el día de corte: su próximo cumpleaños es el del año siguiente.",
      ],
      expectedResult: "Una fecha posterior al 30/06/2026",
      finalFormula: '=FECHA.MES([@Nacimiento],(SIFECHA([@Nacimiento],$M$4,"Y")+1)*12)',
    },
  ],
};
